import React, { useMemo } from 'react';
import SortableTable from '../SortableTable';
import { toTitleCase } from '../../utils/stringUtils';
import { isDraftIssue } from '../../utils/gpsStatusCategories';

const GPSSectorsStats = ({ stats, issues }) => {
  const rows = useMemo(() => {
    const map = new Map();

    // Process all issues to get total counts by sector
    (issues || []).forEach(issue => {
      const sector = issue?.sector || 'Non spécifié';
      const displaySector = toTitleCase(sector);
      if (!map.has(sector)) {
        map.set(sector, {
          sector: displaySector,
          total: 0,
          new: 0,
          ongoing: 0,
          abandoned: 0,
          done: 0,
          brouillons: 0,
          interventionPoints: new Set()
        });
      }

      // Add intervention point to the set for this sector
      if (issue?.intervention_point) {
        map.get(sector).interventionPoints.add(issue.intervention_point);
      }

      // Drafts get their own column (and feed the total), kept out of new/ongoing.
      if (isDraftIssue(issue)) {
        map.get(sector).brouillons += 1;
        map.get(sector).total += 1;
      }
    });

    // Process stats to get specific counts (drafts excluded — counted apart above)
    stats.openTickets.forEach(issue => {
      const sector = issue?.sector || 'Non spécifié';
      if (map.has(sector) && !isDraftIssue(issue)) {
        map.get(sector).new += 1;
      }
    });

    stats.ongoingTickets.forEach(issue => {
      const sector = issue?.sector || 'Non spécifié';
      if (map.has(sector) && !isDraftIssue(issue)) {
        map.get(sector).ongoing += 1;
        map.get(sector).total += 1;
      }
    });

    stats.abandonedTickets.forEach(issue => {
      const sector = issue?.sector || 'Non spécifié';
      if (map.has(sector)) {
        map.get(sector).abandoned += 1;
        map.get(sector).total += 1;
      }
    });

    stats.closedTickets.forEach(issue => {
      const sector = issue?.sector || 'Non spécifié';
      if (map.has(sector)) {
        map.get(sector).done += 1;
        map.get(sector).total += 1;
      }
    });

    // Convert map to array and calculate distinct intervention points count
    return Array.from(map.values()).map(row => ({
      sector: row.sector,
      new: row.new,
      ongoing: row.ongoing,
      done: row.done,
      abandoned: row.abandoned,
      brouillons: row.brouillons,
      distinctsInterventionPoints: row.interventionPoints.size,
      total: row.total
    })).sort((a, b) => b.total - a.total);
  }, [stats, issues]);

  const columns = useMemo(() => ([
    { key: 'sector', label: 'Secteur' },
    { key: 'new', label: 'Nouveaux' },
    { key: 'ongoing', label: 'En cours' },
    { key: 'done', label: 'Fermés' },
    { key: 'abandoned', label: 'Abandonnés' },
    { key: 'brouillons', label: 'Brouillons' },
    { key: 'distinctsInterventionPoints', label: 'Magasins concernés' },
    { key: 'total', label: 'Tickets total' },
  ]), []);

  return (
    <div className="bg-white dark:bg-[#1e1e1e] rounded-lg border border-gray-200 dark:border-gray-900 shadow p-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistiques par secteur</h3>
      <SortableTable
        data={rows}
        columns={columns}
        emptyMessage="Aucune statistique par secteur sur cette période"
        exportable
        exportFilename="gps_secteurs.csv"
        globalSearch
        fullHeight
        withSum
      />
    </div>
  );
};

export default GPSSectorsStats;

